import React, { useEffect, useState } from "react";
import { api, API, BACKEND_URL } from "@/lib/api";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Database, Archive, RotateCcw, ShieldAlert, Copy, Wand2 } from "lucide-react";
import { toast } from "sonner";

export default function Import() {
  const [file, setFile] = useState(null);
  const [magazzinoForce, setMagazzinoForce] = useState(""); // "" | "Vasto" | "Cupello"
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Backup/Restore state
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [autoBackups, setAutoBackups] = useState([]);

  // Manutenzione / USB / Updates state
  const [maintBusy, setMaintBusy] = useState(false);
  const [usbDevices, setUsbDevices] = useState([]);
  const [usbBusy, setUsbBusy] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [updateBusy, setUpdateBusy] = useState(false);

  const refreshUsb = async () => {
    try {
      const { data } = await api.get("/admin/usb/devices");
      setUsbDevices(data.devices || []);
    } catch { setUsbDevices([]); }
  };

  const doBackupUsb = async (device_path) => {
    if (!window.confirm(`Copiare un backup su ${device_path}?`)) return;
    setUsbBusy(true);
    try {
      const { data } = await api.post("/admin/usb/backup", { device_path });
      toast.success(`Backup salvato: ${data.file} (${data.size_human})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore backup USB");
    } finally {
      setUsbBusy(false);
    }
  };

  const checkUpdates = async () => {
    setUpdateBusy(true);
    try {
      const { data } = await api.get("/admin/check-updates");
      setUpdateInfo(data);
      if (data.update_available) toast.success(`Nuova versione disponibile: ${data.latest}`);
      else if (data.error) toast.error(data.error);
      else toast.success("Sei già aggiornato all'ultima versione");
    } catch (err) {
      toast.error("Errore verifica aggiornamenti");
    } finally {
      setUpdateBusy(false);
    }
  };

  const runUpdate = async () => {
    if (!window.confirm("Eseguire l'aggiornamento? Il server si riavvierà.")) return;
    setUpdateBusy(true);
    try {
      const { data } = await api.post("/admin/run-update");
      toast.success(data.message || "Aggiornamento avviato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setUpdateBusy(false);
    }
  };

  useEffect(() => {
    api.get("/admin/backups").then(({ data }) => setAutoBackups(data.backups || [])).catch(() => {});
  }, []);

  const downloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const pin = localStorage.getItem("app_pin") || "";
      const res = await fetch(`${API}/admin/backup`, { headers: { "X-App-Pin": pin } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ts = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `gomme-track_${ts}.archive.gz`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Backup scaricato");
    } catch (err) {
      toast.error("Errore backup: " + (err.message || ""));
    } finally {
      setDownloadingBackup(false);
    }
  };

  const doRestore = async () => {
    if (!restoreFile) return toast.error("Seleziona un file di backup");
    if (restoreConfirm !== "RIPRISTINA") {
      return toast.error('Digita "RIPRISTINA" in maiuscolo per confermare');
    }
    if (!window.confirm("ATTENZIONE: il ripristino sostituirà TUTTI i dati attuali con il backup. Continuare?")) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      fd.append("confirm", restoreConfirm);
      await api.post("/admin/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success("Ripristino completato. Ricarica la pagina.");
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il ripristino");
    } finally {
      setRestoring(false);
    }
  };

  const upload = async () => {
    if (!file) return toast.error("Seleziona un file");
    const fd = new FormData();
    fd.append("file", file);
    if (magazzinoForce) fd.append("magazzino_force", magazzinoForce);
    setUploading(true);
    setResult(null);
    try {
      const { data } = await api.post("/deposits/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Importati ${data.created_deposits} depositi`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'importazione");
    } finally {
      setUploading(false);
    }
  };

  const downloadExport = async (kind) => {
    try {
      const pin = localStorage.getItem("app_pin") || "";
      const url = kind === "pdf" ? `${API}/exports/summary.pdf` : `${API}/exports/all.xlsx`;
      const res = await fetch(url, { headers: { "X-App-Pin": pin } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ts = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = kind === "pdf"
        ? `magazzino-clienti_report_${ts}.pdf`
        : `magazzino-clienti_export_completo_${ts}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(kind === "pdf" ? "Report PDF scaricato" : "Export Excel scaricato");
    } catch (err) {
      toast.error("Errore export: " + (err.message || ""));
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="import-page">
      <div>
        <div className="text-[11px] font-black uppercase tracking-[0.3em] text-[#E60000]">Strumenti</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-1">
          Importa Inventario
        </h1>
        <p className="mt-3 text-base">
          Carica un file <strong>CSV</strong> o <strong>Excel</strong> con i depositi esistenti.
          I clienti vengono creati automaticamente in base a nome + telefono e i duplicati vengono scartati.
        </p>
      </div>

      {/* Export completo */}
      <section className="border-2 border-black bg-white p-6" data-testid="export-full-section">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display font-black text-xl uppercase mb-2">Esporta tutto</h2>
            <p className="text-sm">
              Scarica <strong>TUTTI i dati</strong> del gestionale in un unico file. Include: clienti, depositi, veicoli, magazzino pneumatici e storico modifiche.
            </p>
            <p className="text-xs mt-2 text-black/60">
              Il file <strong>Excel</strong> contiene 5 fogli separati ed è apribile in Microsoft Excel, LibreOffice e Google Sheets (basta caricarlo su Drive).
              Il file <strong>PDF</strong> è un report sintetico con le prime 100 righe per sezione — ideale per stampa.
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => downloadExport("xlsx")}
            data-testid="export-xlsx-button"
            className="inline-flex items-center justify-center gap-2 bg-[#E60000] text-white border-2 border-black px-5 py-3 font-bold uppercase text-xs tracking-widest hover:bg-black"
          >
            <FileSpreadsheet className="w-4 h-4" strokeWidth={3} />
            Scarica tutto (Excel multi-foglio)
          </button>
          <button
            onClick={() => downloadExport("pdf")}
            data-testid="export-pdf-button"
            className="inline-flex items-center justify-center gap-2 bg-black text-white border-2 border-black px-5 py-3 font-bold uppercase text-xs tracking-widest hover:bg-[#E60000]"
          >
            <Download className="w-4 h-4" strokeWidth={3} />
            Scarica report PDF
          </button>
        </div>
      </section>

      <section className="border-2 border-black bg-white p-6">
        <h2 className="font-display font-black text-xl uppercase mb-2">1. Scarica il template</h2>
        <p className="text-sm mb-4">
          Usa questo template come base. Le colonne attese sono: <span className="font-mono-data text-xs">cliente_nome, cliente_telefono, cliente_indirizzo, data_intervento, tipo_intervento, targa, marca_veicolo, modello_veicolo, marca_pneumatico, modello_pneumatico, misura, stagionalita, quantita, magazzino, settore, scaffale, posizione, numero_scheda, numero_scheda_smaltimento, numero_scheda_riconsegna, fuori_tempo_limite, con_cerchio, data_lavorazione, note, stato</span>
        </p>
        <a
          href={`${API}/deposits/template/csv`}
          download
          data-testid="download-template"
          className="inline-flex items-center gap-2 bg-black text-white border-2 border-black px-5 py-3 font-bold uppercase text-xs tracking-widest hover:bg-[#E60000]"
        >
          <Download className="w-4 h-4" strokeWidth={3} />
          Scarica template CSV
        </a>
      </section>

      <section className="border-2 border-black bg-white p-6">
        <h2 className="font-display font-black text-xl uppercase mb-4">2. Carica il file</h2>

        <div className="mb-4">
          <label className="block text-[10px] font-black uppercase tracking-widest mb-2">
            Forza tutti i depositi su questo magazzino
          </label>
          <div className="grid grid-cols-3 gap-2 max-w-md">
            {[
              { val: "", label: "Auto" },
              { val: "Vasto", label: "Vasto" },
              { val: "Cupello", label: "Cupello" },
            ].map((opt) => (
              <button
                key={opt.val || "auto"}
                type="button"
                data-testid={`magazzino-force-${opt.label}`}
                onClick={() => setMagazzinoForce(opt.val)}
                className={`border-2 border-black p-3 font-bold uppercase text-xs tracking-widest transition-colors ${
                  magazzinoForce === opt.val
                    ? "bg-[#E60000] text-white"
                    : "bg-white text-black hover:bg-black hover:text-white"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="text-[10px] mt-2 opacity-70">
            <strong>Auto</strong>: rileva da colonna magazzino o dalla città. <strong>Vasto/Cupello</strong>: forza tutte le righe sul magazzino scelto.
          </div>
        </div>

        <label
          htmlFor="upload-input"
          className="block border-2 border-dashed border-black p-8 text-center cursor-pointer hover:bg-black hover:text-white transition-colors"
          data-testid="upload-dropzone"
        >
          <FileSpreadsheet className="w-12 h-12 mx-auto mb-2" strokeWidth={2} />
          <div className="font-display font-black uppercase">
            {file ? file.name : "Trascina o clicca per selezionare"}
          </div>
          <div className="text-xs mt-1 opacity-70">CSV, XLSX, XLS</div>
          <input
            id="upload-input"
            data-testid="upload-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
          />
        </label>

        <button
          onClick={upload}
          disabled={!file || uploading}
          data-testid="upload-submit"
          className="mt-4 bg-[#E60000] text-white border-2 border-black px-6 py-3 font-bold uppercase tracking-widest hover:bg-black disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Upload className="w-4 h-4" strokeWidth={3} />
          {uploading ? "Importazione..." : "Importa"}
        </button>
      </section>

      {result && (
        <section className="border-2 border-black bg-white p-6" data-testid="import-result">
          <h2 className="font-display font-black text-xl uppercase mb-4">Risultato</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <Stat icon={CheckCircle2} accent="bg-black text-white" label="Depositi creati" value={result.created_deposits} />
            <Stat icon={CheckCircle2} accent="bg-white text-black" label="Clienti creati" value={result.created_clients} />
            <Stat icon={AlertTriangle} accent="bg-[#E60000] text-white" label="Errori" value={result.total_errors} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <Stat icon={AlertTriangle} accent="bg-white text-black" label="Clienti duplicati scartati" value={result.skipped_duplicate_clients} />
            <Stat icon={AlertTriangle} accent="bg-white text-black" label="Operazioni duplicate scartate" value={result.skipped_duplicate_deposits} />
          </div>
          {result.errors?.length > 0 && (
            <div className="border-2 border-black p-3 max-h-60 overflow-auto font-mono-data text-xs">
              {result.errors.map((e, i) => (
                <div key={`${i}-${e}`} className="py-0.5 border-b border-black/20 last:border-b-0">• {e}</div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ==================== Backup / Restore ==================== */}
      <div className="border-t-4 border-black pt-6 mt-10">
        <div className="text-[11px] font-black uppercase tracking-[0.3em] text-[#E60000]">Amministrazione</div>
        <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tighter mt-1">
          Backup, Ripristino & Manutenzione
        </h2>
        <p className="mt-2 text-sm">
          Per uso offline aziendale: i backup automatici giornalieri vengono salvati nella cartella <span className="font-mono-data">./backups/</span> del server.
        </p>
      </div>

      {/* Manutenzione dati: dedup + backfill schede */}
      <section className="border-2 border-black bg-white p-6" data-testid="maintenance-section">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="font-display font-black text-xl uppercase flex items-center gap-2">
              <Wand2 className="w-5 h-5" strokeWidth={3} />
              Pulizia e aggiornamento dati
            </h3>
            <p className="text-sm mt-1">
              Elimina i doppioni e recupera i numeri scheda (ss/sr/ftm) dalle vecchie note dei file importati.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={async () => {
              if (!window.confirm("Cercare ed eliminare i depositi duplicati (stesso cliente + targa + data + misura)?")) return;
              setMaintBusy(true);
              try {
                const { data } = await api.post("/admin/dedup");
                toast.success(`${data.duplicates_removed} doppioni eliminati (${data.groups_examined} gruppi esaminati)`);
              } catch (err) {
                toast.error("Errore dedup: " + (err.response?.data?.detail || err.message));
              } finally { setMaintBusy(false); }
            }}
            disabled={maintBusy}
            data-testid="dedup-deposits-btn"
            className="border-2 border-black bg-white text-black px-4 py-3 font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <Copy className="w-4 h-4" strokeWidth={3} />
            {maintBusy ? "..." : "Trova doppioni depositi"}
          </button>
          <button
            onClick={async () => {
              if (!window.confirm("Cercare ed unificare i clienti duplicati (stesso nome)?")) return;
              setMaintBusy(true);
              try {
                const { data } = await api.post("/admin/dedup-clients");
                toast.success(`${data.duplicates_removed} clienti unificati • ${data.deposits_moved} depositi spostati`);
              } catch (err) {
                toast.error("Errore dedup: " + (err.response?.data?.detail || err.message));
              } finally { setMaintBusy(false); }
            }}
            disabled={maintBusy}
            data-testid="dedup-clients-btn"
            className="border-2 border-black bg-white text-black px-4 py-3 font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <Copy className="w-4 h-4" strokeWidth={3} />
            {maintBusy ? "..." : "Trova clienti doppi"}
          </button>
          <button
            onClick={async () => {
              if (!window.confirm("Rileggere le note di TUTTI i depositi per estrarre numeri scheda ss/sr e flag ftm?")) return;
              setMaintBusy(true);
              try {
                const { data } = await api.post("/admin/backfill-schede");
                toast.success(`${data.updated} depositi aggiornati (${data.total_examined} esaminati)`);
                setTimeout(() => window.location.reload(), 1500);
              } catch (err) {
                toast.error("Errore backfill: " + (err.response?.data?.detail || err.message));
              } finally { setMaintBusy(false); }
            }}
            disabled={maintBusy}
            data-testid="backfill-btn"
            className="border-2 border-black bg-[#E60000] text-white px-4 py-3 font-bold uppercase text-xs tracking-widest hover:bg-black disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <Wand2 className="w-4 h-4" strokeWidth={3} />
            {maintBusy ? "..." : "Estrai schede da note"}
          </button>
        </div>
      </section>

      {/* USB backup */}
      <section className="border-2 border-black bg-white p-6" data-testid="usb-section">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="font-display font-black text-xl uppercase flex items-center gap-2">
              <Database className="w-5 h-5" strokeWidth={3} />
              Backup su chiavetta USB
            </h3>
            <p className="text-sm mt-1">
              Inserisci una chiavetta USB nel PC server, clicca <strong>Rileva chiavette</strong> e poi <strong>Salva</strong> sulla periferica desiderata.
            </p>
          </div>
          <button
            onClick={refreshUsb}
            data-testid="refresh-usb-btn"
            className="border-2 border-black bg-black text-white px-4 py-2 font-bold uppercase text-xs tracking-widest hover:bg-[#E60000] inline-flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" strokeWidth={3} />
            Rileva chiavette
          </button>
        </div>
        {usbDevices.length === 0 ? (
          <div className="border-2 border-dashed border-black p-4 text-center text-sm font-bold uppercase">
            Nessuna chiavetta rilevata. Inserisci una USB nel PC server e clicca "Rileva chiavette".
          </div>
        ) : (
          <div className="space-y-2">
            {usbDevices.map((d) => (
              <div key={d.path} className="flex items-center justify-between border-2 border-black p-3">
                <div>
                  <div className="font-display font-black uppercase">{d.name}</div>
                  <div className="font-mono-data text-xs opacity-70">
                    {d.path} • {d.free_human} liberi / {d.total_human}
                  </div>
                </div>
                <button
                  onClick={() => doBackupUsb(d.path)}
                  disabled={usbBusy}
                  data-testid={`usb-backup-${d.name}`}
                  className="bg-[#E60000] text-white border-2 border-black px-4 py-2 font-bold uppercase text-xs tracking-widest hover:bg-black disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <Download className="w-4 h-4" strokeWidth={3} />
                  {usbBusy ? "Copia..." : "Salva backup"}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Updates */}
      <section className="border-2 border-black bg-white p-6" data-testid="updates-section">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="font-display font-black text-xl uppercase flex items-center gap-2">
              <Wand2 className="w-5 h-5" strokeWidth={3} />
              Aggiornamenti software
            </h3>
            <p className="text-sm mt-1">
              Controlla se è disponibile una nuova versione del gestionale.
            </p>
          </div>
          <button
            onClick={checkUpdates}
            disabled={updateBusy}
            data-testid="check-updates-btn"
            className="border-2 border-black bg-black text-white px-4 py-2 font-bold uppercase text-xs tracking-widest hover:bg-[#E60000] disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" strokeWidth={3} />
            {updateBusy ? "..." : "Controlla aggiornamenti"}
          </button>
        </div>
        {updateInfo && (
          <div className="border-2 border-black p-3 space-y-2 text-sm">
            <div>Versione installata: <strong className="font-mono-data">{updateInfo.current}</strong></div>
            <div>Versione disponibile: <strong className="font-mono-data">{updateInfo.latest}</strong></div>
            {updateInfo.update_available ? (
              <>
                {updateInfo.release_notes && (
                  <div className="border-t border-black/20 pt-2">
                    <div className="text-[10px] font-black uppercase tracking-widest mb-1">Novità</div>
                    <div className="text-xs whitespace-pre-line">{updateInfo.release_notes}</div>
                  </div>
                )}
                {updateInfo.auto_update_enabled ? (
                  <button
                    onClick={runUpdate}
                    disabled={updateBusy}
                    data-testid="run-update-btn"
                    className="bg-[#E60000] text-white border-2 border-black px-4 py-2 font-bold uppercase text-xs tracking-widest hover:bg-black disabled:opacity-50"
                  >
                    {updateBusy ? "Aggiornamento in corso..." : "Installa e riavvia"}
                  </button>
                ) : (
                  <div className="text-xs italic opacity-70">
                    Aggiornamento automatico disattivato. Aggiorna manualmente via SSH:
                    <code className="block font-mono-data mt-1 text-[10px]">docker compose pull &amp;&amp; docker compose up -d</code>
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs font-bold text-[#E60000]">
                {updateInfo.error || "Sei già aggiornato all'ultima versione."}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Download backup manuale */}
      <section className="border-2 border-black bg-white p-6" data-testid="backup-section">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="font-display font-black text-xl uppercase flex items-center gap-2">
              <Archive className="w-5 h-5" strokeWidth={3} />
              Backup completo manuale
            </h3>
            <p className="text-sm mt-1">
              Scarica un archivio compresso (.archive.gz) con TUTTI i dati attuali (clienti + depositi).
              Conservalo in un posto sicuro (chiavetta USB, altro PC, cloud privato).
            </p>
          </div>
          <Database className="w-12 h-12" strokeWidth={2} />
        </div>
        <button
          onClick={downloadBackup}
          disabled={downloadingBackup}
          data-testid="download-backup-btn"
          className="bg-black text-white border-2 border-black px-6 py-3 font-bold uppercase tracking-widest hover:bg-[#E60000] disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Download className="w-4 h-4" strokeWidth={3} />
          {downloadingBackup ? "Generazione..." : "Scarica backup"}
        </button>
      </section>

      {/* Lista backup automatici */}
      {autoBackups.length > 0 && (
        <section className="border-2 border-black bg-white p-6" data-testid="auto-backups-section">
          <h3 className="font-display font-black text-xl uppercase mb-3">
            Backup automatici recenti ({autoBackups.length})
          </h3>
          <div className="font-mono-data text-xs max-h-48 overflow-auto border-2 border-black">
            {autoBackups.map((b) => (
              <div key={b.name} className="grid grid-cols-3 gap-2 p-2 border-b border-black/20 last:border-b-0">
                <span className="truncate">{b.name}</span>
                <span className="text-right">{(b.size_bytes / 1024).toFixed(1)} KB</span>
                <span className="text-right opacity-70">{new Date(b.modified).toLocaleString("it-IT")}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Restore */}
      <section className="border-2 border-[#E60000] bg-white p-6" data-testid="restore-section">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-[#E60000] text-white flex items-center justify-center border-2 border-black flex-shrink-0">
            <ShieldAlert className="w-5 h-5" strokeWidth={3} />
          </div>
          <div>
            <h3 className="font-display font-black text-xl uppercase">Ripristina backup</h3>
            <p className="text-sm mt-1">
              <strong>ATTENZIONE</strong>: il ripristino sostituirà <strong>tutti i dati attuali</strong> con quelli del file caricato.
              Operazione irreversibile. Esegui prima un backup manuale.
            </p>
          </div>
        </div>

        <label
          htmlFor="restore-input"
          className="block border-2 border-dashed border-black p-6 text-center cursor-pointer hover:bg-black hover:text-white transition-colors mb-3"
        >
          <RotateCcw className="w-10 h-10 mx-auto mb-2" strokeWidth={2} />
          <div className="font-display font-black uppercase text-sm">
            {restoreFile ? restoreFile.name : "Clicca per selezionare file .archive.gz"}
          </div>
          <input
            id="restore-input"
            data-testid="restore-input"
            type="file"
            accept=".gz,.archive,.archive.gz"
            onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
            className="hidden"
          />
        </label>

        <label className="block text-[10px] font-black uppercase tracking-widest mb-1.5">
          Per confermare digita esattamente: <span className="text-[#E60000]">RIPRISTINA</span>
        </label>
        <input
          data-testid="restore-confirm"
          type="text"
          value={restoreConfirm}
          onChange={(e) => setRestoreConfirm(e.target.value)}
          placeholder="RIPRISTINA"
          className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data font-black tracking-widest uppercase mb-3"
        />

        <button
          onClick={doRestore}
          disabled={!restoreFile || restoring || restoreConfirm !== "RIPRISTINA"}
          data-testid="restore-btn"
          className="bg-[#E60000] text-white border-2 border-black px-6 py-3 font-bold uppercase tracking-widest hover:bg-black disabled:opacity-50 inline-flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" strokeWidth={3} />
          {restoring ? "Ripristino in corso..." : "Ripristina backup"}
        </button>
      </section>
    </div>
  );
}

const Stat = ({ icon: Icon, accent, label, value }) => (
  <div className="border-2 border-black p-4">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 flex items-center justify-center border-2 border-black ${accent}`}>
        <Icon className="w-5 h-5" strokeWidth={2.5} />
      </div>
      <div>
        <div className="font-display font-black text-2xl">{value ?? 0}</div>
        <div className="text-[10px] font-black uppercase tracking-widest">{label}</div>
      </div>
    </div>
  </div>
);
